<template>
  <NewBsBox :bs="wrapperBs">
    <div class="page-settings">
      <PagerOption
        v-if="showPagerOption"
        :model-value="modelValue.pageSize"
        :size-list="sizeList"
        @update:modelValue="onPageSize"
      />
      <div>
        共 <template v-if="showTotal">
          {{ modelValue.total }} 条，
        </template>{{ pageCount }} 页
      </div>
    </div>

    <div class="page-actions">
      <!-- // minimal variant -->
      <template v-if="variant === 'minimal'">
        <Pager
          :disabled="disablePrev"
          :icon-bs="{ transform: 'rotate(180deg)' }"
          icon="arrow-right-center-m"
          @click="goPage(modelValue.pageNo - 1)"
        />
        <PagerInput
          :model-value="modelValue.pageNo"
          :max-value="pageCount"
          :show-jump="true"
          @update:modelValue="(val) => goPage(val)"
        >
          <template #right>
            <Pager
              :disabled="disableNext"
              icon="arrow-right-center-m"
              @click="goPage(modelValue.pageNo + 1)"
            />
          </template>
        </PagerInput>
      </template>

      <!-- // noraml variant -->
      <template v-else>
        <Pager
          :disabled="disablePrev"
          :icon-bs="{ transform: 'rotate(180deg)' }"
          :aria-label="`上一页`"
          icon="arrow-right-center-m"
          @click="goPage(modelValue.pageNo - 1)"
        />
        <Pager
          v-for="p of pageList"
          :key="p"
          :aria-label="`第${p}页`"
          :is-active="p === modelValue.pageNo"
          @click="goPage(p)"
        >
          {{ p }}
        </Pager>
        <Pager
          :disabled="disableNext"
          :aria-label="`下一页`"
          icon="arrow-right-center-m"
          @click="goPage(modelValue.pageNo + 1)"
        />
        <div class="page-jump">
          跳转至
          <PagerInput
            :model-value="modelValue.pageNo"
            :max-value="pageCount"
            :show-jump="false"
            @update:modelValue="(val) => goPage(val)"
          />
          页
        </div>
      </template>
    </div>
  </NewBsBox>
</template>

<script lang="ts">
  import {
    defineComponent, PropType,
    computed,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { merge } from 'lodash'
  import Pager from './Pager.vue'
  import PagerOption from './PagerOption.vue'
  import PagerInput from './PagerInput.vue'
  import { BeerStyleObj } from '../../../core'

type PageData = {
  total: number
  pageNo: number
  pageSize: number
}

type AlignType = 'space-between' | 'flex-start' | 'flex-end'

  export default defineComponent({
    name: 'Pagination',
    components: {
      NewBsBox,
      Pager,
      PagerOption,
      PagerInput,
    },
    props: {
      /**
       * 容器样式
       * @ignore
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否显示 total 的数量
       * true - ‘共 xx 条，xx 页’
       * flase - ‘共 xx 页’
       */
      showTotal: {
        type: Boolean,
        default: true,
      },
      /**
       * 样式风格类型，有 normal|minimal
       * - minimal 只能输入页面翻页
       * - normal 可以点击临近的 pager
       */
      variant: {
        type: String,
        default: 'normal', // also minimal
      },
      /**
       * 页码和翻页设置两个部件的水平 flex 排列方式
       */
      align: {
        type: String as PropType<AlignType>,
        default: 'space-between',
      },
      /**
       * 每页显示的条数下拉选择列表设置
       * 如果传入的 PageSize 不在里面，会被合并进去
       */
      sizeList: {
        type: Array,
        default: () => ([10, 20, 30, 50]),
      },
      /**
       * 翻页器的 v-model 选项
       * 但是其实更建议，modelValue 和 update:modelValue 来拆开写
       * update:modelValue 触发后，翻页(API)成功了再去设置 modelvalue
       * 防止翻页数据脏了而数据显示之前的状态
       */
      modelValue: {
        type: Object as PropType<PageData>,
        default: () => ({
          total: 0,
          pageNo: 1,
          pageSize: 10,
        }),
      },
      /**
       * 控制 PagerOption 是否显示 默认显示
       */
      showPagerOption: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const wrapperBs = computed(() => merge({
        fontSize: '14px',
        width: '100%',
        display: 'flex',
        wordBreak: 'keep-all',
        justifyContent: props.align,
        '.page-settings': {
          display: 'flex', alignItems: 'center', flexShrink: 0,
        },
        '.page-actions': {
          ml: 16, display: 'flex', alignItems: 'center',
        },
        '.page-jump': {
          display: 'flex',
          alignItems: 'center',
          ml: 8,
        },
      }, props.bs))
      const pageCount = computed(() => Math.max(
        Math.ceil(props.modelValue.total / props.modelValue.pageSize),
        1,
      ))
      const pageList = computed(() => {
        let init = Number(props.modelValue.pageNo)
        if (init < 1) {
          init = 1
        }
        if (init > pageCount.value) {
          init = pageCount.value
        }
        let lst = [init]
        '1234'
          .split('')
          .map(i => Number(i))
          .forEach((i: number) => {
            const nextVal = lst[lst.length - 1] + 1
            const prevVal = lst[0] - 1
            let isAppend = false
            let isPrepend = false
            if (i % 2 === 1) {
              if (nextVal <= pageCount.value) {
                isAppend = true
              } else if (prevVal >= 1) {
                isPrepend = true
              }
            } else {
              if (prevVal >= 1) { // eslint-disable-line no-lonely-if
                isPrepend = true
              } else if (nextVal <= pageCount.value) {
                isAppend = true
              }
            }
            if (isAppend) {
              lst.push(nextVal)
            }
            if (isPrepend) {
              lst = [prevVal, ...lst]
            }
          })
        return lst
      })
      const disablePrev = computed(() => props.modelValue.pageNo <= 1)
      const disableNext = computed(() => props.modelValue.pageNo >= pageCount.value)

      function onChangeAttr(attr: string) {
        return (value: any) => {
          const newV = {
            ...props.modelValue,
            [attr]: value,
          }
          /**
           * 当设置改变后，重置页码成 1
           */
          if (attr !== 'pageNo') {
            newV.pageNo = 1
          }
          /**
           * 当翻页动作触发后的事件
           * @property {object} modelValue - 将要改变的 modelValue 对象
           */
          emit('update:modelValue', newV)
        }
      }
      const onPageNo = onChangeAttr('pageNo')
      const onPageSize = onChangeAttr('pageSize')
      return {
        wrapperBs,
        pageCount,
        pageList,
        disableNext,
        disablePrev,
        onPageSize,
        goPage: (val: number) => {
          const pageNo = Number(val)
          if (pageNo === props.modelValue.pageNo) {
            return
          }
          if (pageNo < 1 || pageNo > pageCount.value) {
            return
          }
          onPageNo(pageNo)
        },
      }
    },
  })
</script>
